/** Zeitpunkte der Slot-Suche in eine zeitzonen-behaftete Form bringen.
 *
 *  Warum es diese Datei gibt: `POST /calendar/polls` verlangt einen Beginn MIT
 *  Zeitzone (`app/schemas/calendar_poll.py::_require_aware`) — eine naive Angabe
 *  ist mehrdeutig und wird mit 422 abgewiesen, noch bevor der Handler laeuft.
 *  Die Slot-Suche liefert aber genau so einen naiven Wert: der email-service
 *  formatiert mit `_local_naive` (`api/sb_calendar.py:106-108`) und schickt
 *  z.B. "2026-08-03T13:00:00" ohne Offset. Der Wizard reichte ihn unveraendert
 *  durch — deshalb schlug jede Abstimmung ab zwei Vorschlaegen fehl, waehrend
 *  der Ein-Slot-Pfad (`POST /calendar/events`, dort `start: str` ohne Pruefung)
 *  weiterlief.
 *
 *  ANNAHME, die hier bewusst getroffen wird: der naive Wert ist **Berliner
 *  Ortszeit**, und der Browser des Nutzers laeuft in derselben Zone. Deshalb
 *  wird er als Ortszeit des Browsers gelesen (`new Date(...)`) und via
 *  `toISOString()` nach UTC gerechnet. Fuer ein Haus-Werkzeug mit Geschaefts-
 *  zeiten in Deutschland trifft das zu; ein Browser in einer anderen Zone
 *  wuerde den Termin um die Zonendifferenz verschieben.
 *
 *  Warum NICHT das mitgelieferte `timezone`-Feld ausgewertet wird, obwohl es im
 *  Slot steht: es traegt "W. Europe Standard Time" (`services/calendar.py:21`),
 *  eine **Windows**-Zonen-ID. Weder `Date` noch `Intl` koennen die lesen; es
 *  braeuchte eine CLDR-Tabelle Windows→IANA als zusaetzliche Abhaengigkeit,
 *  deren einziger real vorkommender Wert auf `Europe/Berlin` zeigt — also genau
 *  auf die Zone, die der Browser hier ohnehin hat. Der Aufwand kauft nichts.
 *
 *  Was hier ausdruecklich NICHT passiert: ein angehaengtes "Z". Das waere ein
 *  stiller Zwei-Stunden-Versatz (13:00 Berlin wuerde zu 15:00 Berlin) und damit
 *  schlimmer als der Fehler, den diese Datei behebt — Termine laegen zur
 *  falschen Stunde, ohne dass jemand eine Fehlermeldung saehe.
 */

/** Zeitzonen-Kennung am Ende: "Z", "+02:00" oder "+0200".
 *
 *  Trifft eine naive Angabe nicht: "2026-08-03T13:00:00" endet auf ":00", also
 *  ohne Vorzeichen. */
const HAS_TIMEZONE = /(?:Z|[+-]\d{2}:?\d{2})$/i;

/** Naive Ortszeit → zeitzonen-behaftete ISO-Zeit. Bereits behaftete Werte
 *  bleiben unveraendert.
 *
 *  Die Idempotenz ist nicht Kosmetik, sondern notwendig: `pickedSlots` ist eine
 *  GEMISCHTE Liste. Die Vorschlaege aus der Suche sind naiv, die selbst
 *  eingetragenen Zeiten aus `CustomSlotEntry` sind ueber `toISOString()` schon
 *  UTC. Ein zweites Umrechnen wuerde die zweite Sorte um den Zonen-Offset
 *  verschieben. */
export function toOffsetIso(value: string): string {
  const trimmed = value.trim();
  if (HAS_TIMEZONE.test(trimmed)) return value;

  const parsed = new Date(trimmed);
  if (Number.isNaN(parsed.getTime())) {
    // Lieber lautstark scheitern als eine unbrauchbare Zeit verschicken: der
    // Aufrufer faengt das und zeigt einen Fehler. Den Wert einfach
    // durchzureichen hiesse, den behobenen Fehler wieder einzubauen.
    throw new RangeError(`Unlesbarer Zeitpunkt: ${value}`);
  }
  return parsed.toISOString();
}

/** Die `slots`-Nutzlast fuer `POST /calendar/polls`.
 *
 *  Nur der Beginn — das Ende rechnet der Server aus `duration_minutes`
 *  (`PollSlotIn`), damit Slot-Laenge und Termin-Laenge nicht auseinanderlaufen. */
export function pollSlotsPayload(
  slots: readonly { start: string }[]
): { starts_at: string }[] {
  return slots.map((s) => ({ starts_at: toOffsetIso(s.start) }));
}
